import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Avatar, Box, Stack, Tooltip } from '@mui/material';

import ImagePlaceHolder from '../../assets/meeting-cards/placeholder-journey-meeting.png';
import palette from '../../theme/palette';
import Checkbox from '../Checkbox';
import {
  CardContainer,
  MeetingTitle,
  AvatarGroup,
  SummaryHover,
  ImageBox,
  ImageTopLayer,
  ContentLayer,
  BorderLayer,
  ContentWithBackground
} from './MeetingCardStyle';
import SummaryToolTipContent from './SummaryTooltipContent';

type CalendarMeetingInfoDtoType =
  (typeof CalendarMeetingInfoDtoType)[keyof typeof CalendarMeetingInfoDtoType];

type Meeting = {
  id?: string;
  type?: CalendarMeetingInfoDtoType;
  sharedMeetingId?: string;
  title?: string;
  calendarEventId?: string;
  calendarMasterEventId?: string;
  startTime?: string;
  thumbnail?: string;
  participants?: {
    userId?: string;
    email?: string;
    name?: string;
  }[];
  transcript?: {
    fileKey?: string;
    bucketName?: string;
  };
  createdAt?: string;
  updatedAt?: string;
};

const CalendarMeetingInfoDtoType = {
  myMeeting: 'myMeeting',
  publicShared: 'publicShared',
  privateShared: 'privateShared'
} as const;

type MeetingCardProps = {
  meeting: Meeting;
  isLoadingSummary?: boolean;
  summary?: string;
  onSummaryHover: (id: string) => void;
  onClick?: () => void;
  isChecked?: boolean;
  onCheckboxClick?: (meeting: Meeting) => void;
};

const avatarColors = [
  palette.blue[800],
  palette.blue[900],
  palette.blue[1000],
  palette.blue[1100],
  palette.blue[1200]
];

const MeetingCard = ({
  meeting,
  onSummaryHover,
  isLoadingSummary,
  onClick,
  summary,
  isChecked,
  onCheckboxClick
}: MeetingCardProps) => {
  const handleCheckboxClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (meeting.id) {
      onCheckboxClick?.(meeting);
    }
  };

  return (
    <CardContainer onClick={onClick}>
      <BorderLayer />
      <ContentLayer isChecked={!!isChecked}>
        <ContentWithBackground>
          <Checkbox checked={!!isChecked} onClick={handleCheckboxClick} />
          <ImageBox>
            <ImageTopLayer />
            <img
              style={{ height: '100%', objectFit: 'fill', width: '100%', position: 'absolute' }}
              src={
                meeting.thumbnail ? `data:image/jpg;base64,${meeting.thumbnail}` : ImagePlaceHolder
              }
              alt={meeting.title}
              onError={e => {
                e.currentTarget.src = ImagePlaceHolder;
              }}
            />
          </ImageBox>
          <Stack sx={{ padding: '16px', flex: 1 }}>
            <MeetingTitle>{meeting.title}</MeetingTitle>
            <Stack flex={1} direction="row" alignItems="end" justifyContent="space-between">
              <Tooltip
                open={isLoadingSummary || !!summary}
                title={<SummaryToolTipContent summary={summary} isLoading={isLoadingSummary} />}
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: palette.black,
                      padding: '8px',
                      color: palette.white
                    }
                  }
                }}
              >
                <SummaryHover
                  onMouseEnter={() => meeting.id && onSummaryHover(meeting.id)}
                  onMouseLeave={() => onSummaryHover('')}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '14px' }} /> Summary
                </SummaryHover>
              </Tooltip>
              <Box>
                <AvatarGroup max={4} spacing={6}>
                  {meeting.participants?.map((participant, index) => (
                    <Avatar
                      key={`${participant.email}-${index}`}
                      sx={{
                        color: `${avatarColors[Math.floor(Math.random() * avatarColors.length)]} !important`
                      }}
                    >
                      <span>
                        {participant?.email?.at(0)?.toUpperCase()}
                        {participant?.email?.at(1)?.toUpperCase()}
                      </span>
                    </Avatar>
                  ))}
                </AvatarGroup>
              </Box>
            </Stack>
          </Stack>
        </ContentWithBackground>
      </ContentLayer>
    </CardContainer>
  );
};

export default MeetingCard;
